import { useCallback, useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import styled from 'styled-components';

import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs/inputText';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { spacing, Subtitle2 } from 'ui/theme';
import { MakeCopy } from 'ui/icons/action/MakeCopy';
import { Confirm24 } from 'ui/icons/action/Confirm24';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { useTranslations } from '../../hooks/useTranslations';

const UrlSection = styled.div`
  align-items: flex-start;
  display: flex;
  column-gap: ${spacing.half};
  margin-bottom: ${spacing.half};

  > *:first-child {
    flex: 1 1 auto;
  }

  > a:first-child {
    margin-right: ${spacing.half};
  }

  button {
    flex: 0 0 auto;
    white-space: nowrap;
    width: min-content;
  }
`;

export const inputId = 'mi-url-input-field';

export const testIds = {
  copyBtn: 'mi-panel-url-input-copy-btn',
  copyBtnCopiedIcon: 'mi-panel-url-input-copy-btn-copied',
  copyBtnCopyIcon: 'mi-panel-url-input-copy-btn-copy',
  inputField: inputId,
};

type Props = {
  canCopy?: boolean;
  label?: string;
  url?: string;
};

export const UrlField = ({ url = '', label = 'URL', canCopy = false }: Props) => {
  const { t } = useTranslations();
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>();

  // Show 'copied' indicator, then revert after 3 seconds
  const onCopy = useCallback(() => {
    if (copiedTimer.current) {
      clearTimeout(copiedTimer.current);
    }
    setIsCopied(true);
    copiedTimer.current = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (copiedTimer.current) {
        clearTimeout(copiedTimer.current);
      }
    };
  }, []);

  return (
    <section>
      <Subtitle2>{label}</Subtitle2>
      <UrlSection>
        <FormField inputId={inputId}>
          <InputText
            data-test={testIds.inputField}
            id={inputId}
            isReadOnly
            placeholder={t('input.url.placeholder')}
            value={url}
          />
        </FormField>
        {canCopy && (
          <Tooltip
            tooltipContent={t(`tooltip.${isCopied ? 'copied' : 'copy'}`)}
            open={isCopied || undefined}
          >
            <CopyToClipboard text={url} onCopy={onCopy} data-test={testIds.copyBtn}>
              <IconButton
                isDisabled={false}
                size={BUTTON_SIZES.LARGE}
                variant={BUTTON_VARIANTS.SECONDARY}
              >
                {isCopied ? (
                  <Confirm24
                    className="shape"
                    data-test={testIds.copyBtnCopiedIcon}
                    height="24"
                    isColorInherited
                    width="24"
                  />
                ) : (
                  <MakeCopy height="24" width="24" data-test={testIds.copyBtnCopyIcon} />
                )}
              </IconButton>
            </CopyToClipboard>
          </Tooltip>
        )}
      </UrlSection>
    </section>
  );
};
